import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../css/others/FilterPage.css";
import SQLQueryGenerator from "../others/SQLQueryGenerator";
import SaveFilterPopup from "../popups/SaveFilterPopup";
import FavoriteFiltersPopup from "../popups/FavoriteFiltersPopup";
import { RuleGroupType, RuleType } from "react-querybuilder";
import { apiCall } from "../main/ApiCall";
import ToastPopup from "../popups/ToastPopup";
import "../css/others/Table.css";
import EncryptionUtils from "../main/encryptionUtils";
import { createToastMessage, ToastMessage } from "../interFaces/interfaces";
const FilterPage: React.FC = () => {
  const [isSaveFilterPopupOpen, setIsSaveFilterPopupOpen] = useState(false);
  const [isFavoriteFiltersPopupOpen, setIsFavoriteFiltersPopupOpen] = useState(false);
  const [favoriteFilters, setFavoriteFilters] = useState<{ name: string; filters: string | { field: string; operator: string; value: string }[]; }[]>([]);
  const [filterName, setFilterName] = useState<string>("");
  const [attributes, setAttributes] = useState<{ name: string; label: string; type: string }[]>([]);
  const [query, setQuery] = useState<RuleGroupType>({ combinator: "and", rules: [], });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const { campaignId } = useParams<{ campaignId: string }>();
  const clientId = localStorage.getItem("clientId");
  const userInfo = EncryptionUtils.get<any>("userInfo", null);
  const userId = userInfo?.id;
  const [value] = useState<string>("");
  const navigate = useNavigate();
  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        const response = await apiCall("get", `/api/client-apis?clientId=${clientId}`);
        const fieldsObject = response.attributeToLabelMapping;
        const mappedAttributes = Object.entries(fieldsObject).map(([key, label]) => ({ name: label as string, label: key, type: key.toLowerCase().includes("date") ? "date" : "string", }));
        setAttributes(mappedAttributes);
      } catch (error) {
        console.error("Failed to fetch attributes:", error);
      }
    };
    fetchAttributes();
  }, [clientId]);
  useEffect(() => {
    apiCall("get", `/api/filters/${userId}`)
      .then((response) => {
        if (Array.isArray(response)) {
          setFavoriteFilters(response.map(filter => ({ id: filter.id, name: filter.name, filters: filter.filters })));
        }
      })
      .catch((error) => console.error("Error fetching favorite filters:", error));
  }, [userId]);
  const handleAddFavoriteFilter = () => {
    if (filterName.trim() === "") {
      setMessage(createToastMessage("Please enter a name for the filter.", "warning", 3000));
      return;
    }
    const structuredFilters = query.rules
      .filter((rule): rule is RuleType => { return "field" in rule && "value" in rule && rule.field && rule.value })
      .map((rule) => ({ field: rule.field, operator: rule.operator || "=", value: rule.value, }));
    if (structuredFilters.length === 0) {
      setMessage(createToastMessage("No valid filters to save.", "warning", 3000));
      return;
    }
    const favoritePayload = { name: filterName, filters: structuredFilters, isFavorite: true, };
    apiCall("post", `/api/filters/${userId}`, favoritePayload)
      .then(() => {
        setFavoriteFilters([...favoriteFilters, { name: filterName, filters: structuredFilters },]);
        setFilterName("");
        toggleSaveFilterPopup();
        setMessage(createToastMessage("Favorite filter saved successfully!", "success", 3000));
        setTimeout(() => { window.location.reload() }, 3000);
      })
      .catch((error: any) => {
        const errorMessage = error?.response?.data?.message;
        setMessage(createToastMessage(errorMessage, "error", 3000));
      });
  };
  const toggleSaveFilterPopup = () => { setIsSaveFilterPopupOpen(!isSaveFilterPopupOpen); };
  const toggleFavoriteFiltersPopup = () => { setIsFavoriteFiltersPopupOpen(!isFavoriteFiltersPopupOpen); };
  const handleApplyFavoriteFilter = (favoriteFilter: any) => {
    try {
      const parsedFilters = Array.isArray(favoriteFilter.filters) ? favoriteFilter.filters : JSON.parse(favoriteFilter.filters);
      const newRules = parsedFilters.map(({ field, operator, value }: any) => ({ field, operator, value, }));
      setQuery((prevQuery) => ({ ...prevQuery, rules: [...prevQuery.rules, ...newRules], }));
      toggleFavoriteFiltersPopup();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      setMessage(createToastMessage(errorMessage, "error", 3000));
    }
  };
  const handleRemoveFavoriteFilter = async (index: number) => {
    const filterToRemove = favoriteFilters[index];
    if (!("id" in filterToRemove)) {
      setMessage(createToastMessage("Error: Filter ID not found.", "warning", 3000));
      return;
    }
    const userId = filterToRemove.id;
    try {
      await apiCall("patch", `/api/filters/${userId}`, { isFavorite: false });
      setFavoriteFilters((prevFilters) => prevFilters.filter((_, i) => i !== index));
      setMessage(createToastMessage("Favorite filter removed.", "success", 3000));
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      setMessage(createToastMessage(errorMessage, "error", 3000));
    }
  };
  const handleShowResults = () => {
    const transformFieldName = (fieldName: string): string => {
      return fieldName
        .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => index === 0 ? match.toLowerCase() : match.toUpperCase())
        .replace(/\s+/g, "");
    };
    const formatDate = (date: string): string => {
      if (!date.includes("-")) return date;
      const [year, month, day] = date.split("-");
      return `${day}-${month}-${year}`;
    };
    const filterData = query.rules.flatMap((rule) => {
      if ("field" in rule) {
        const transformedField = transformFieldName(rule.field);
        if (rule.operator === "between" && Array.isArray(rule.value)) {
          const [startDate, endDate] = rule.value;
          return [
            { field: transformedField, operator: ">", value: formatDate(startDate) }, { field: transformedField, operator: "<", value: formatDate(endDate) }
          ];
        }
        const formattedValue = rule.value === "date" || transformedField.toLowerCase().includes("date") ? formatDate(rule.value as string) : !isNaN(Number(rule.value)) ? Number(rule.value) : rule.value;
        return {
          field: transformedField, operator: rule.operator || "=", value: formattedValue,
        };
      }
      return [];
    });
    navigate(`/campaign-details/${campaignId}`, { state: { filterData } });
  };
  useEffect(() => {
    if (inputRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.focus();
    }
  }, [value]);
  const handleResetFilters = () => { setQuery({ combinator: "and", rules: [] }); };
  const handleGoBack = () => {
    navigate(-1);
  };
  return (
    <div className="filter-container">
      <div className="filter-container-box">
        <h2 className="filter-title">Filters</h2>
        <button className="toggle-sidebar-btn" onClick={toggleFavoriteFiltersPopup}>Show Favorite Filters</button>
      </div>
      <div className="main-content">
        <div className="filter-fields">
          <div className="filter-fields-box">
            <p className="filter-header-p">Select your filters</p>
            <button className="button-reset" onClick={handleResetFilters}>Reset Filters</button>
          </div>
          <SQLQueryGenerator attributes={attributes} onQueryChange={setQuery} query={query} />
          <div className="button-container">
            <button className="button-back" onClick={handleGoBack}>Back</button>
            {query.rules.length > 0 && (
              <button className="button-save" onClick={toggleSaveFilterPopup}>Save Favorite</button>
            )}
            <button className="button-show-results" onClick={handleShowResults}>Show Results</button>
          </div>
        </div>
      </div>
      <SaveFilterPopup isOpen={isSaveFilterPopupOpen} filterName={filterName} setFilterName={setFilterName} togglePopup={toggleSaveFilterPopup} onSave={handleAddFavoriteFilter} />
      <FavoriteFiltersPopup isOpen={isFavoriteFiltersPopupOpen} favoriteFilters={favoriteFilters} onApply={handleApplyFavoriteFilter} onRemove={handleRemoveFavoriteFilter} onClose={toggleFavoriteFiltersPopup} />
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default FilterPage;